import { useEffect } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAppDispatch } from 'hooks';

import {
  apiApplicationTextValidatorLog,
  apiCreateApplicationTextValidatorLog,
} from 'api';

import { Alert, AlertIcon, Box, Button, Spinner } from '@chakra-ui/react';

import { AI_TEXT_REMARKS_TYPE_MAPPER } from 'variables/constants';

import { APISubmissionTextValidatorLogData, APIRemark } from 'types/api.types';

import { updateRemark as updateRemarkAuto } from 'reducers/autoPersonalFormReducer';

import { updateRemark as updateRemarkHome } from 'reducers/homePersonalFormReducer';

const ValidateText = ({
  remark,
  uuid,
  validationId,
  validationTypeKey,
  itemKey,
  formStateKey,
  context,
}: {
  remark: APIRemark;
  uuid: string;
  validationId: string;
  validationTypeKey?: string;
  itemKey: string;
  formStateKey: 'autoPersonalForm' | 'homePersonalForm';
  context?: string;
}) => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const updateRemarkReducer =
    formStateKey === 'autoPersonalForm' ? updateRemarkAuto : updateRemarkHome;

  const { mutate: createValidatorLog, isPending: isPendingInitialLog } =
    useMutation({
      mutationFn: () =>
        apiCreateApplicationTextValidatorLog(uuid, {
          text: remark.text,
          context,
          validation_key: validationId,
          item_key: itemKey,
          type: AI_TEXT_REMARKS_TYPE_MAPPER[
            (validationTypeKey ||
              validationId) as keyof typeof AI_TEXT_REMARKS_TYPE_MAPPER
          ],
        }),
      onSuccess: (data: { text_validator_log_id: number }) => {
        dispatch(
          updateRemarkReducer({
            validation_key: validationId,
            item_key: itemKey,
            data: {
              text_validator_log_id: data.text_validator_log_id,
              status:
                data.text_validator_log_id === null ? 'approved' : 'validating',
            },
          }),
        );
      },
    });

  const { data: validatorLog } = useQuery({
    queryKey: ['text-validator-log', uuid, remark.text_validator_log_id],
    queryFn: () =>
      apiApplicationTextValidatorLog(uuid, remark.text_validator_log_id!),
    enabled: remark.status === 'validating',
    refetchInterval: (query) => {
      const data = query.state.data as APISubmissionTextValidatorLogData;
      if (!data) return 2000;
      if (data.status !== 'validating') return false;
      return 2000;
    },
  });

  useEffect(() => {
    if (validatorLog && validatorLog.status !== 'validating') {
      dispatch(
        updateRemarkReducer({
          validation_key: validationId,
          item_key: itemKey,
          data: {
            ai_result: validatorLog.result,
            status: validatorLog.status,
          },
        }),
      );
    }

    return () => {
      queryClient.removeQueries({
        queryKey: ['text-validator-log', uuid, remark.text_validator_log_id],
      });
    };
  }, [validatorLog, dispatch, validationId, itemKey]);

  const handleValidateClick = () => {
    createValidatorLog();
  };

  const getAlertStatus = (remark: APIRemark) => {
    return remark.status === 'approved' ? 'success' : 'error';
  };

  const getAlertResult = (remark: APIRemark) => {
    if (remark.ai_result) return remark.ai_result;
    if (remark.status === 'approved') {
      return 'Remark approved';
    }
    return 'Unhandled';
  };

  const isLoading = remark.status === 'validating' || isPendingInitialLog;
  const showAlert =
    !isLoading && (remark.status == 'approved' || remark.status === 'failed');

  return (
    <Box mt="10px">
      {remark.status !== 'approved' && (
        <Button
          onClick={handleValidateClick}
          isDisabled={isLoading || remark.text.length === 0}
          variant="brand"
        >
          {isLoading ? <Spinner size="sm" mr={2} /> : null}
          Validate accuracy
        </Button>
      )}

      {showAlert && (
        <Alert status={getAlertStatus(remark)} mt="10px">
          <AlertIcon />
          {getAlertResult(remark)}
        </Alert>
      )}
    </Box>
  );
};

export default ValidateText;
